import React, { useState, useEffect } from "react";
import {
  Box,
  Text,
  Heading,
  VStack,
  HStack,
  Badge,
  Select,
  Input,
  Button,
  useColorModeValue,
  SimpleGrid,
  useToast,
  Spinner,
  Center,
  Alert,
  AlertIcon,
  Flex,
} from "@chakra-ui/react";
import {
  FaCalendarAlt,
  FaUserPlus,
  FaUsers,
  FaTimes,
  FaFileAlt,
  FaFilter,
  FaSync,
} from "react-icons/fa";
import { getActivities } from "../services/activityService";
import JalaliDateDisplay from "../components/JalaliDateDisplay";
import ActivityDetailModal from "../components/ActivityDetailModal";
import Pagination from "../components/Pagination";

function Activities() {
  const textColor = useColorModeValue("gray.700", "white");
  const bgCard = useColorModeValue("white", "gray.700");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const hoverBgColor = useColorModeValue("gray.50", "gray.600");
  const toast = useToast();

  // State management
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    type: "",
    startDate: "",
    endDate: "",
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  // دریافت فعالیت‌ها
  const fetchActivities = async () => {
    try {
      setLoading(true);
      const params = {
        ...filters,
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm
      };
      const response = await getActivities(params);
      setActivities(response.data?.activities || []);
      setTotalItems(response.data?.pagination?.totalItems || 0);
      setTotalPages(response.data?.pagination?.totalPages || 1);
    } catch (error) {
      console.error("خطا در دریافت فعالیت‌ها:", error);
      // برای تست، داده‌های نمونه اضافه می‌کنیم
      setActivities([
        {
          _id: '1',
          type: 'appointment',
          action: 'created',
          message: 'ملاقات جدید برای مراجع احمدی با مشاور محمدی رزرو شد',
          createdBy: { name: 'مدیر سیستم' },
          createdAt: new Date(),
          details: { appointmentId: '123', clientName: 'احمدی', consultantName: 'محمدی' }
        },
        {
          _id: '2',
          type: 'client',
          action: 'created',
          message: 'مراجع جدید فاطمه احمدی ثبت شد',
          createdBy: { name: 'کاربر' },
          createdAt: new Date(Date.now() - 86400000),
          details: { clientId: '456', clientName: 'فاطمه احمدی' }
        },
        {
          _id: '3',
          type: 'consultant',
          action: 'updated',
          message: 'اطلاعات مشاور علی رضایی بروزرسانی شد',
          createdBy: { name: 'مدیر سیستم' },
          createdAt: new Date(Date.now() - 172800000),
          details: { consultantId: '789', consultantName: 'علی رضایی' }
        }
      ]);
      toast({
        title: "هشدار",
        description: "خطا در اتصال به سرور - نمایش داده‌های نمونه",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // بارگذاری اولیه
  useEffect(() => {
    fetchActivities();
  }, [filters, currentPage, itemsPerPage, searchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

  // تغییر فیلترها
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // پاک کردن فیلترها
  const clearFilters = () => {
    setFilters({
      type: "",
      startDate: "",
      endDate: "",
    });
    setSearchTerm("");
    setCurrentPage(1);
  };

  // توابع مدیریت pagination
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // بازگشت به صفحه اول
  };

  // فیلتر کردن فعالیت‌ها بر اساس جستجو
  const filteredActivities = activities.filter(activity =>
    activity.message?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    activity.createdBy?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    getActivityTypeText(activity.type).toLowerCase().includes(searchTerm.toLowerCase())
  );

  // انتخاب آیکون بر اساس نوع فعالیت
  const getActivityIcon = (type) => {
    switch (type) {
      case 'appointment':
        return FaCalendarAlt;
      case 'client':
        return FaUserPlus;
      case 'consultant':
        return FaUsers;
      case 'cancellation':
        return FaTimes;
      default:
        return FaFileAlt;
    }
  };

  // انتخاب رنگ بر اساس نوع فعالیت
  const getActivityColor = (type) => {
    switch (type) {
      case 'appointment':
        return "blue";
      case 'client':
        return "green";
      case 'consultant':
        return "purple";
      case 'cancellation':
        return "red";
      case 'user':
        return "orange";
      case 'payment':
        return "teal";
      default:
        return "gray";
    }
  };

  // تبدیل نوع فعالیت به فارسی
  const getActivityTypeText = (type) => {
    switch (type) {
      case 'appointment':
        return 'ملاقات';
      case 'client':
        return 'مراجع';
      case 'consultant':
        return 'مشاور';
      case 'user':
        return 'کاربر';
      case 'payment':
        return 'پرداخت';
      case 'system':
        return 'سیستم';
      case 'cancellation':
        return 'لغو';
      default:
        return type;
    }
  };

  // تبدیل عملیات به فارسی
  const getActionText = (action) => {
    switch (action) {
      case 'created':
        return 'ایجاد شد';
      case 'updated':
        return 'بروزرسانی شد';
      case 'deleted':
        return 'حذف شد';
      case 'completed':
        return 'تکمیل شد';
      case 'cancelled':
        return 'لغو شد';
      case 'scheduled':
        return 'برنامه‌ریزی شد';
      case 'logged_in':
        return 'ورود';
      case 'logged_out':
        return 'خروج';
      default:
        return action;
    }
  };

  return (
    <Box >
      {/* Header */}
      <Box mb="20px">
        <Heading size="lg" color={textColor} mb="10px">
          مدیریت فعالیت‌ها
        </Heading>
        <Text color="gray.500" fontSize="md">
          مشاهده تمام فعالیت‌های انجام شده در سیستم
        </Text>
      </Box>



      {/* فیلترها */}
      <Box bg={bgCard} borderRadius="15px" boxShadow="0 20px 27px 0 rgb(0 0 0 / 5%)" mb="30px" p="20px">
        <Box pb="10px" mb="20px">
          <Flex justify="space-between" align="center">
            <HStack>
              <FaFilter />
              <Text fontWeight="bold" color={textColor}>
                فیلترها
              </Text>
            </HStack>
            <HStack spacing={3}>
              <Button
                size="sm"
                colorScheme="blue"
                leftIcon={<FaSync />}
                onClick={() => {
                  fetchActivities();
                }}
              >
                بروزرسانی
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={clearFilters}
              >
                پاک کردن
              </Button>
            </HStack>
          </Flex>
        </Box>
        <Box pt="10px">
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing="20px">
            <Box>
              <Text fontSize="sm" color="gray.500" mb="2">
                نوع فعالیت
              </Text>
              <Select
                value={filters.type}
                onChange={(e) => handleFilterChange('type', e.target.value)}
                placeholder="همه انواع"
                size="sm"
              >
                <option value="appointment">ملاقات</option>
                <option value="client">مراجع</option>
                <option value="consultant">مشاور</option>
                <option value="user">کاربر</option>
                <option value="payment">پرداخت</option>
                <option value="system">سیستم</option>
                <option value="cancellation">لغو</option>
              </Select>
            </Box>



            <Box>
              <Text fontSize="sm" color="gray.500" mb="2">
                جستجو
              </Text>
              <Input
                placeholder="جستجو در پیام‌ها..."
                size="sm"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Box>
          </SimpleGrid>
        </Box>
      </Box>

      {/* لیست فعالیت‌ها */}
      <Box bg={bgCard} borderRadius="15px" boxShadow="0 20px 27px 0 rgb(0 0 0 / 5%)" p="20px">
        <Box pb="10px" mb="20px">
          <Flex justify="space-between" align="center">
            <Text fontWeight="bold" color={textColor}>
              فعالیت‌های اخیر
            </Text>
            <Text fontSize="sm" color="gray.500">
              {filteredActivities.length} از {activities.length} فعالیت
            </Text>
          </Flex>
        </Box>
        <Box pt="10px">
          {loading ? (
            <Center py="50px">
              <Spinner size="lg" />
            </Center>
          ) : filteredActivities.length === 0 ? (
            <Alert status="info">
              <AlertIcon />
              {searchTerm ? 'هیچ فعالیتی با این جستجو یافت نشد' : 'هیچ فعالیتی یافت نشد'}
            </Alert>
          ) : (
            <VStack spacing={4} align="stretch">
              {filteredActivities.map((activity, index) => {
                const IconComponent = getActivityIcon(activity.type);
                const iconColor = getActivityColor(activity.type);
                
                return (
                  <Box
                    key={activity._id || index}
                    p="20px"
                    border="1px solid"
                    borderColor={borderColor}
                    borderRadius="10px"
                    _hover={{ bg: hoverBgColor }}
                    transition="all 0.2s"
                    cursor="pointer"
                    onClick={() => {
                      setSelectedActivity(activity);
                      setIsDetailModalOpen(true);
                    }}
                  >
                    <Flex justify="space-between" align="start">
                      <Flex align="center" flex={1}>
                        <Box
                          p="10px"
                          borderRadius="50%"
                          bg={`${iconColor}.100`}
                          color={`${iconColor}.600`}
                          mr="15px"
                        >
                          <IconComponent size="16px" />
                        </Box>
                        <Box flex={1}>
                          <Flex align="center" mb="5px">
                            <Text fontWeight="bold" color={textColor} mr="10px">
                              {activity.message}
                            </Text>
                            <Badge colorScheme={iconColor} size="sm">
                              {getActivityTypeText(activity.type)}
                            </Badge>
                            <Badge colorScheme="gray" size="sm" ml="5px">
                              {getActionText(activity.action)}
                            </Badge>
                          </Flex>
                          <Text fontSize="sm" color="gray.500" mb="5px">
                            توسط: {activity.createdBy?.name || 'سیستم'}
                          </Text>
                          <Text fontSize="xs" color="gray.400">
                            <JalaliDateDisplay date={activity.createdAt} />
                          </Text>
                        </Box>
                      </Flex>
                    </Flex>
                    
                    {activity.details && Object.keys(activity.details).length > 0 && (
                      <Box mt="15px" pt="15px" borderTop="1px solid" borderColor={borderColor}>
                        <Text fontSize="sm" color="gray.600" mb="8px">
                          جزئیات:
                        </Text>
                        <SimpleGrid columns={2} spacing={2}>
                          {Object.entries(activity.details).slice(0, 4).map(([key, value]) => (
                            <Text key={key} fontSize="xs" color="gray.500">
                              <strong>{key}:</strong> {value || 'نامشخص'}
                            </Text>
                          ))}
                          {Object.keys(activity.details).length > 4 && (
                            <Text fontSize="xs" color="blue.500" cursor="pointer">
                              مشاهده جزئیات بیشتر...
                            </Text>
                          )}
                        </SimpleGrid>
                      </Box>
                    )}
                  </Box>
                );
              })}
            </VStack>
          )}
        </Box>

        {/* Pagination */}
        {totalPages > 1 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            isLoading={loading}
          />
        )}
      </Box>

      {/* مودال نمایش جزئیات فعالیت */}
      <ActivityDetailModal
        isOpen={isDetailModalOpen}
        onClose={() => {
          setIsDetailModalOpen(false);
          setSelectedActivity(null);
        }}
        activity={selectedActivity}
      />
    </Box>
  );
}

export default Activities; 